import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import {
  CreditApplicationStatus, Sentiment, SentimentStyle,
} from './graphql/types/credit-application';
import {
  StylePreferences,
  HeaderStyle,
  HEADER_STYLE,
  BUTTON_CASING,
  TAB_CASING,
} from './contexts/style-preferences-context';

// A custom theme for this app
export const COLORS = {
  darkerGreen: '#4C675E',
  darkGreen: '#006400',
  darkGreen2: '#008b00',
  lightGreen: '#AFD4AB',
  lightGreen2: '#1CB01C',
  transparentGreen: 'rgba(144, 238, 144, 0.2)',

  darkerYellow: '#8B6914',
  darkYellow: '#F4B400',
  mediumDarkYellow: '#FF9800',
  lightYellow: '#F8D66D',
  transparentYellow: 'rgba(255, 223, 128, 0.2)',
  backgroundYellow: 'rgba(255, 152, 0, 0.08)',

  mediumOrange: '#a86802',
  darkOrange: '#EA5A00',

  darkerRed: '#8B0000',
  darkRed: '#cc0000',
  darkRed2: '#e00000',
  errorRed: '#f44336',
  lightRed: '#E08080',
  lighterRed: '#ecb1b1',
  transparentRed: 'rgba(255, 99, 99, 0.2)',
  backgroundRed: 'rgba(244, 67, 54, 0.08)',

  darkerGray: '#4D4D4D',
  darkGray: '#666666',
  mediumDarkGray: '#808080',
  mediumGray: '#ccc',
  lightGray: '#E0E0E0',
  lighterGray: '#f5f5f5',
  lightestGray: '#f9fbfc',
  hoverGray: 'rgba(0, 0, 0, 0.04)',
  borderGray: '#EEF1F2',

  lightestBlue: '#D1D4FF',
  lightBlue: '#767AE5',
  darkBlue: '#565BDF',
  darkerBlue: '#2125AB',
  backgroundBlue: 'rgba(86, 91, 223, 0.08)',

  // Purple theme (from palette #796AE8, #D7D2F8)
  lightestPurple: '#D7D2F8',
  lightPurple: '#a699f0',
  darkPurple: '#796AE8',
  darkerPurple: '#5c4bc7',

  // Teal theme (from palette #538FB1, #C8DBE5)
  lightestTeal: '#C8DBE5',
  lightTeal: '#7aabca',
  darkTeal: '#538FB1',
  darkerTeal: '#3d6d89',

  // Gold/Amber theme (from palette #E3B974, #E8D8AF)
  lightestGold: '#E8D8AF',
  lightGold: '#dfbf82',
  darkGold: '#E3B974',
  darkerGold: '#d4a84d',

  // Deep Green/Forest theme (from palette #006A60, #E4EDDC)
  lightestForest: '#E4EDDC',
  lightForest: '#a8c9c0',
  darkForest: '#006A60',
  darkerForest: '#004d45',

  black: '#2E2C3C',
  white: '#FFFFFF',

  legacyUIPurple: 'rgb(146, 146, 204)',
  legacyUITurquoise: 'rgb(132, 225, 188)',
  legacyUIYellow: 'rgb(252, 233, 106)',
  legacyUIOrange: 'rgb(240, 139, 82)',
  legacyUIRed: 'rgb(240, 82, 82)',
  legacyDbtTurquoise: 'rgb(94, 216, 195)',
};

export const PAYMENT_CHART_COLORS = {
  current: COLORS.darkGreen2,
  '1to30': COLORS.lightGreen2,
  '31to60': COLORS.darkYellow,
  '61to90': COLORS.darkOrange,
  '91+': COLORS.darkRed2,
};

// Simplified status color configuration - uses opacity for hover states
export const STATUS_COLORS: Record<CreditApplicationStatus, {
  primary: string;
}> = {
  [CreditApplicationStatus.IN_PROGRESS]: {
    primary: COLORS.mediumDarkGray,
  },
  [CreditApplicationStatus.AWAITING_SIGNATURE]: {
    primary: COLORS.darkGray,
  },
  [CreditApplicationStatus.SUBMITTED]: {
    primary: COLORS.darkTeal,
  },
  [CreditApplicationStatus.AWAITING_THIRD_PARTIES]: {
    primary: COLORS.mediumDarkYellow,
  },
  [CreditApplicationStatus.READY_FOR_REVIEW]: {
    primary: COLORS.darkBlue,
  },
  [CreditApplicationStatus.APPROVED]: {
    primary: COLORS.darkGreen2,
  },
  // Same as APPROVED for now
  [CreditApplicationStatus.APPROVED_WITH_CHANGES]: {
    primary: COLORS.darkGreen2,
  },
  [CreditApplicationStatus.REJECTED]: {
    primary: COLORS.darkRed,
  },
};

export const SENTIMENT_COLORS: Record<Sentiment, SentimentStyle> = {
  positive: {
    background: '#EBFAF0',
    border: '#C4DDCB',
    color: COLORS.darkGreen2,
  },
  negative: {
    background: '#FFF6F6',
    border: '#FACFCE',
    color: COLORS.darkRed,
  },
  neutral: {
    background: '#FEFCE1',
    border: '#FFE884',
    color: COLORS.darkerYellow,
  },
};

export const DEFAULT_CHIP_SX = {
  fontSize: {
    xs: '0.65rem',
    sm: '0.7rem',
    md: '0.75rem',
  },
  color: 'black',
};

let theme = createTheme({
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h6: {
      fontWeight: 'bold',
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.MuiAlert-filledInfo': {
            backgroundColor: COLORS.darkBlue,
            color: 'white',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFF',
        },
        columnHeader: {
          backgroundColor: '#FFF',
        },
        columnHeaderTitle: {
          fontWeight: 'bold',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: 'black',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: COLORS.darkBlue,
            backgroundColor: 'white',
          },
          '&.Mui-selected:hover': {
            backgroundColor: COLORS.transparentGreen,
          },
          '&:hover': {
            backgroundColor: COLORS.transparentGreen,
          },
        },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        childLeaving: {
          color: COLORS.darkGreen,
        },
        ripple: {
          color: COLORS.darkGreen,
        },
        ripplePulsate: {
          color: COLORS.darkGreen,
        },
        rippleVisible: {
          color: COLORS.darkGreen,
        },
        child: {
          color: COLORS.darkGreen,
        },
        childPulsate: {
          color: COLORS.darkGreen,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: {
          '&.dark-yellow-bar': { backgroundColor: COLORS.darkYellow },
          '&.dark-green-bar': { backgroundColor: COLORS.darkGreen },
          '&.dark-gray-bar': { backgroundColor: COLORS.darkGray },
          '&.light-gray-bar': { backgroundColor: COLORS.lightGray },
        },
        root: {
          '&.dark-yellow-root': { backgroundColor: COLORS.darkYellow },
          '&.light-gray-root': { backgroundColor: COLORS.lightGray },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
        head: {
          fontWeight: 'bold',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        // Tabs are bold app-wide; their casing is controlled by the user's style preference
        // (see buildTheme).
        root: {
          fontWeight: 'bold',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          '&.trend-increasing': {
            border: 0,
            color: COLORS.darkGreen,
            '& .MuiChip-icon': {
              color: COLORS.darkGreen,
              fontSize: '1rem',
            },
          },
          '&.trend-increasing-swapped': {
            border: `1px solid ${COLORS.darkRed}`,
            color: COLORS.darkRed,
            '& .MuiChip-icon': {
              color: COLORS.darkRed,
              fontSize: '1rem',
            },
          },
          '&.trend-flat': {
            border: 0,
            color: COLORS.black,
            '& .MuiChip-icon': {
              color: COLORS.black,
              fontSize: '1rem',
            },
          },
          '&.trend-flat-swapped': {
            border: `1px solid ${COLORS.black}`,
            color: COLORS.black,
            '& .MuiChip-icon': {
              color: COLORS.black,
              fontSize: '1rem',
            },
          },
          '&.trend-decreasing': {
            border: 0,
            color: COLORS.darkRed,
            '& .MuiChip-icon': {
              color: COLORS.darkRed,
              fontSize: '1rem',
            },
          },
          '&.trend-decreasing-swapped': {
            border: `1px solid ${COLORS.darkGreen}`,
            color: COLORS.darkGreen,
            '& .MuiChip-icon': {
              color: COLORS.darkGreen,
              fontSize: '1rem',
            },
          },
          '&.score-high': {
            backgroundColor: COLORS.transparentGreen,
            borderStyle: 'solid',
            borderColor: COLORS.darkGreen,
            borderWidth: 1,
          },
          '&.score-medium': {
            backgroundColor: COLORS.transparentYellow,
            borderStyle: 'solid',
            borderColor: COLORS.darkYellow,
            borderWidth: 1,
          },
          '&.score-low': {
            backgroundColor: COLORS.transparentRed,
            borderStyle: 'solid',
            borderColor: COLORS.darkRed,
            borderWidth: 1,
          },
          '&.score-null': {
            borderStyle: 'solid',
            borderColor: COLORS.black,
            borderWidth: 1,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.info-icon': {
            fontSize: 'inherit',
            color: COLORS.darkBlue,
            cursor: 'pointer',
            position: 'relative',
            top: '0.125em',
            '&:hover': {
              color: COLORS.darkerBlue,
            },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 1,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-label': {
            fontWeight: 400,
            color: COLORS.darkGray,
            '&.Mui-active': {
              fontWeight: 600,
              color: COLORS.black,
            },
            '&.Mui-completed': {
              fontWeight: 600,
            },
          },
          '& .MuiStepIcon-root': {
            width: 32,
            height: 32,
            fontSize: '1rem',
            '&.Mui-active': {
              color: COLORS.darkPurple,
            },
            '&.Mui-completed': {
              color: COLORS.darkPurple,
            },
          },
          '& .MuiStepConnector-root': {
            top: 16,
            left: 'calc(-50% + 16px)',
            right: 'calc(50% + 16px)',
            '& .MuiStepConnector-line': {
              borderTopWidth: 2,
            },
          },
        },
      },
    },
  },

  // https://mui.com/material-ui/customization/palette/
  palette: {
    primary: {
      main: COLORS.darkBlue,
    },
    secondary: {
      main: COLORS.lightBlue,
    },
    error: {
      main: COLORS.darkRed,
    },
    success: {
      main: COLORS.darkGreen2,
    },
    warning: {
      main: COLORS.lightYellow,
    },
    text: {
      primary: COLORS.black,
    },
  },
});
theme = responsiveFontSizes(theme);

// Layer the user-selectable style preferences (header background/weight, button casing, tab
// casing) on top of the base theme. Called with the current preferences so the whole app
// restyles when a preference changes.
// Header cell background + weight for a header-style preset. Shared by the theme (DataGrid +
// plain MUI tables) and the material-react-table grids, which override the theme's head styles
// and so must set the background through muiTableHeadCellProps directly.
export const resolveHeaderCellStyle = (headerStyle: HeaderStyle) => ({
  backgroundColor: headerStyle === HEADER_STYLE.WHITE_BOLD ? '#FFF' : COLORS.lighterGray,
  fontWeight: headerStyle === HEADER_STYLE.GRAY_REGULAR ? 'normal' : 'bold',
});

export const buildTheme = (preferences: StylePreferences) => {
  const {
    backgroundColor: headerBackgroundColor,
    fontWeight: headerFontWeight,
  } = resolveHeaderCellStyle(preferences.headerStyle);
  const buttonTextTransform = preferences.buttonCasing === BUTTON_CASING.TITLE ? 'none' : 'uppercase';
  const tabTextTransform = preferences.tabCasing === TAB_CASING.TITLE ? 'none' : 'uppercase';
  return createTheme(theme, {
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: buttonTextTransform },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: tabTextTransform },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          // Applies to material-react-table and plain MUI table headers (DataGrid headers are
          // styled via MuiDataGrid below).
          head: {
            fontWeight: headerFontWeight,
            backgroundColor: headerBackgroundColor,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          columnHeader: { backgroundColor: headerBackgroundColor },
          columnHeaderTitle: { fontWeight: headerFontWeight },
        },
      },
    },
  });
};

export default theme;
